// Slice 40 — mono chip with semantic tone. Used everywhere a tier/status
// chip appears.

import type { ReactNode } from "react";

export type HqBadgeTone =
  | "neutral"
  | "accent"
  | "success"
  | "warn"
  | "danger"
  | "pro"
  | "beta"
  | "free";

export interface HqBadgeProps {
  tone?: HqBadgeTone;
  size?: "sm" | "md" | "lg";
  mono?: boolean;
  children: ReactNode;
}

interface Tone {
  bg: string;
  color: string;
  border: string;
}

const TONES: Record<HqBadgeTone, Tone> = {
  neutral: {
    bg: "var(--hq-bg-input)",
    color: "var(--hq-text-muted)",
    border: "var(--hq-border-input)",
  },
  accent: {
    bg: "var(--hq-accent-soft)",
    color: "var(--hq-accent)",
    border: "rgba(34,211,238,.25)",
  },
  success: {
    bg: "var(--hq-success-soft)",
    color: "var(--hq-success)",
    border: "rgba(52,211,153,.25)",
  },
  warn: { bg: "var(--hq-warn-soft)", color: "var(--hq-warn)", border: "rgba(251,191,36,.25)" },
  danger: {
    bg: "var(--hq-danger-soft)",
    color: "var(--hq-danger)",
    border: "rgba(251,113,133,.25)",
  },
  pro: {
    bg: "rgba(167,139,250,.10)",
    color: "var(--hq-tier-pro)",
    border: "rgba(167,139,250,.28)",
  },
  beta: {
    bg: "var(--hq-accent-soft)",
    color: "var(--hq-tier-beta)",
    border: "rgba(34,211,238,.25)",
  },
  free: { bg: "#15171c", color: "var(--hq-tier-free)", border: "var(--hq-border-input)" },
};

const SIZES = {
  sm: { h: 18, px: 6, fs: 10 },
  md: { h: 20, px: 7, fs: 10.5 },
  lg: { h: 24, px: 9, fs: 11.5 },
} as const;

export function HqBadge({ children, tone = "neutral", size = "md", mono = true }: HqBadgeProps) {
  const t = TONES[tone];
  const s = SIZES[size];
  return (
    <span
      className={mono ? "hq-mono" : "hq-sans"}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        height: s.h,
        padding: `0 ${s.px}px`,
        borderRadius: "var(--hq-radius-md)",
        background: t.bg,
        color: t.color,
        border: `1px solid ${t.border}`,
        fontSize: s.fs,
        fontWeight: 500,
        letterSpacing: ".02em",
      }}
    >
      {children}
    </span>
  );
}
